import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useHeader } from '@/contexts';
import {
  useTotaisImpostosTradicionais,
  useTotaisIBSCBS,
  useHistoricoMensal,
  type FiltrosGestaoImpostos,
} from '@/services/GestaoImpostos';
import {
  calcularProjecaoCompleta,
  PRIMEIRO_ANO_TRANSICAO,
  ULTIMO_ANO_TRANSICAO,
  type TotaisBaseProjecao,
} from '@/lib/tax/cronogramaTransicaoLC214';
import { GestaoImpostosContext } from '@/contexts/GestaoImpostosContext';

const FILTROS_VAZIOS: FiltrosGestaoImpostos = { empresaId: null, dataInicio: null, dataFim: null };

/** Ano do cronograma a usar para "Situação Atual": o ano do fim do período filtrado, limitado a [2026, 2033] — um período fora da janela da LC 214/2025 (ex.: histórico anterior a 2026) usa a borda mais próxima do cronograma. */
function anoSituacaoAtual(dataFim: string | null | undefined): number {
  const ano = dataFim ? new Date(dataFim).getFullYear() : PRIMEIRO_ANO_TRANSICAO;
  if (Number.isNaN(ano)) return PRIMEIRO_ANO_TRANSICAO;
  return Math.min(Math.max(ano, PRIMEIRO_ANO_TRANSICAO), ULTIMO_ANO_TRANSICAO);
}

export function GestaoImpostosProvider({ children }: PropsWithChildren) {
  const { filtros } = useHeader();

  const [filtrosAplicados, setFiltrosAplicados] = useState<FiltrosGestaoImpostos>(FILTROS_VAZIOS);
  const [buscaAplicada, setBuscaAplicada] = useState(false);
  const [aliquotaIS, setAliquotaIS] = useState(0);

  const aplicarTokenRef = useRef(0);
  const [aplicarToken, setAplicarToken] = useState(0);

  const selecaoMensalValida = useMemo(
    () =>
      !!filtros.empresaId &&
      filtros.periodoModo === 'mesano' &&
      !!filtros.dataInicio &&
      !!filtros.dataFim,
    [filtros.dataFim, filtros.dataInicio, filtros.empresaId, filtros.periodoModo]
  );

  const {
    data: totaisTradicionais,
    isLoading: isLoadingTradicionais,
    isFetching: isFetchingTradicionais,
    refetch: refetchTradicionais,
  } = useTotaisImpostosTradicionais(filtrosAplicados, { enabled: false });

  const {
    data: totaisIBSCBS,
    isLoading: isLoadingIBSCBS,
    isFetching: isFetchingIBSCBS,
    refetch: refetchIBSCBS,
  } = useTotaisIBSCBS(filtrosAplicados, { enabled: false });

  const { historico, isLoading: isLoadingHistorico } = useHistoricoMensal(
    filtrosAplicados,
    12,
    buscaAplicada && !!filtrosAplicados.empresaId
  );

  useEffect(() => {
    if (!buscaAplicada) return;
    if (aplicarToken === 0) return;
    if (aplicarTokenRef.current === aplicarToken) return;
    aplicarTokenRef.current = aplicarToken;
    refetchTradicionais();
    refetchIBSCBS();
  }, [aplicarToken, buscaAplicada, refetchTradicionais, refetchIBSCBS]);

  const handleBuscar = useCallback(() => {
    if (!selecaoMensalValida) {
      setBuscaAplicada(false);
      return;
    }

    setFiltrosAplicados({
      empresaId: filtros.empresaId,
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim,
    });
    setBuscaAplicada(true);
    setAplicarToken(prev => prev + 1);
  }, [filtros.dataFim, filtros.dataInicio, filtros.empresaId, selecaoMensalValida]);

  /*
    Decisão 11 da SPEC-017: depois de um "Buscar" no cabeçalho, as OUTRAS telas abrem já
    buscadas com o mesmo recorte, sem pedir de novo. `filtros.aplicado` é o que diz que
    houve aquele clique — e ele se desfaz quando a pessoa mexe no recorte, então isto não
    dispara enquanto ela escolhe.

    `buscaAplicada` na condição é o que impede o laço: `handleBuscar` o liga, e com ele
    ligado este efeito para de agir.
  */
  useEffect(() => {
    if (!filtros.aplicado) return;
    if (buscaAplicada) return;
    if (!selecaoMensalValida) return;
    handleBuscar();
  }, [filtros.aplicado, buscaAplicada, selecaoMensalValida, handleBuscar]);

  const handleLimparFiltrosGlobais = useCallback(() => {
    setBuscaAplicada(false);
    setFiltrosAplicados(FILTROS_VAZIOS);
    setAplicarToken(0);
    aplicarTokenRef.current = 0;
  }, []);

  const isLoadingSituacaoAtual =
    isLoadingTradicionais || isFetchingTradicionais || isLoadingIBSCBS || isFetchingIBSCBS;

  const totaisBase: TotaisBaseProjecao | null = useMemo(() => {
    if (!totaisTradicionais || !totaisIBSCBS) return null;
    return {
      totalVendas: totaisIBSCBS.totalVendas,
      icms: totaisTradicionais.icms,
      ipi: totaisTradicionais.ipi,
      pisCofins: totaisTradicionais.pis + totaisTradicionais.cofins,
      ibs: totaisIBSCBS.ibs,
      cbs: totaisIBSCBS.cbs,
      is: (totaisIBSCBS.totalVendas * aliquotaIS) / 100,
    };
  }, [totaisTradicionais, totaisIBSCBS, aliquotaIS]);

  const projecaoCompleta = useMemo(
    () => (totaisBase ? calcularProjecaoCompleta(totaisBase) : null),
    [totaisBase]
  );

  const projecaoAnoAtual = useMemo(() => {
    if (!projecaoCompleta) return null;
    const ano = anoSituacaoAtual(filtrosAplicados.dataFim);
    return projecaoCompleta.anos.find(item => item.ano === ano) ?? null;
  }, [projecaoCompleta, filtrosAplicados.dataFim]);

  const value = useMemo(
    () => ({
      selecaoMensalValida,
      buscaAplicada,
      handleBuscar,
      handleLimparFiltrosGlobais,
      filtrosAplicados,
      isLoadingSituacaoAtual,
      totalVendas: totaisIBSCBS?.totalVendas ?? null,
      projecaoAnoAtual,
      projecaoCompleta,
      aliquotaIS,
      setAliquotaIS,
      historico,
      isLoadingHistorico,
    }),
    [
      selecaoMensalValida,
      buscaAplicada,
      handleBuscar,
      handleLimparFiltrosGlobais,
      filtrosAplicados,
      isLoadingSituacaoAtual,
      totaisIBSCBS,
      projecaoAnoAtual,
      projecaoCompleta,
      aliquotaIS,
      historico,
      isLoadingHistorico,
    ]
  );

  return <GestaoImpostosContext.Provider value={value}>{children}</GestaoImpostosContext.Provider>;
}
